import type { Cell } from "./cells";

// The two databases the page can be pointed at, and the words the control above the editor is drawn from.
export const MODES = [{ value: "sqlite", label: "SQLite" }, { value: "postgres", label: "PostgreSQL" }] as const;

export type ModeId = typeof MODES[number]["value"];

export function isMode(value: unknown): value is ModeId {
  return MODES.some((mode) => mode.value === value);
}

// What the engine is called while it is still being fetched, which is the whole of what the loading state says.
export const ENGINE_NAMES: Record<ModeId, string> = { sqlite: "SQLite", postgres: "PostgreSQL" };

// One database, open and seeded. Neither implementation is imported until a mode asks for it.
export interface Engine {
  // What the database calls itself, which is the first line of every session's log.
  readonly version: string;
  // One statement, run whole. Anything the database refused is thrown with the message it gave.
  execute(sql: string): Promise<Outcome>;
  // The catalogue as the tree draws it, read back after every run because a statement may have rewritten it.
  inspect(): Promise<Schema[]>;
  close(): Promise<void>;
}

// What one statement came back with: the grid it drew, what the database called it, and anything it said on the way.
export interface Outcome {
  columns: string[];
  rows: Cell[][];
  // The command tag SQL answers with — SELECT, INSERT, CREATE — or null where the engine does not report one.
  command: string | null;
  affected: number | null;
  // Postgres RAISE NOTICE and friends; SQLite has no such channel and always answers with an empty list.
  notices: string[];
}

// A namespace of relations: Postgres has as many as somebody creates, SQLite has the databases it has attached.
export interface Schema {
  name: string;
  relations: Relation[];
}

export interface Relation {
  name: string;
  // "table", "view", "materialized view" — spelled as the database spells it rather than as a code the tree decodes.
  kind: string;
  columns: Column[];
  indexes: Index[];
  constraints: Constraint[];
  // A view's own SELECT, which is the one piece of a relation's definition neither catalogue keeps as columns.
  definition: string | null;
}

export interface Column {
  name: string;
  type: string;
  notNull: boolean;
  primaryKey: boolean;
  fallback: string | null;
}

export interface Index {
  name: string;
  unique: boolean;
  primary: boolean;
  columns: string[];
}

export interface Constraint {
  name: string;
  // PRIMARY KEY, FOREIGN KEY, UNIQUE, CHECK: the words the standard uses, since the tree prints them as they are.
  kind: string;
  detail: string;
}

// The whole of the code splitting: neither engine's megabytes are in the chunk this page loads, and the mode that is
// showing is the only one ever fetched. Every database opens empty and none of them is handed a seed: a reset opens a
// second one and drops the first, and what fills either is the script in the editor, run through `execute` like any
// other — so the tables a dataset builds are logged and answered for statement by statement rather than appearing.
export async function openDatabase(mode: ModeId): Promise<Engine> {
  const open = mode === "sqlite" ? (await import("./sqlite")).openSqlite : (await import("./postgres")).openPostgres;
  return open();
}

// Whether anything has been made in this database, which the Load button asks before it empties one and the editor's
// completion asks before it offers a name: Postgres answers with `public` whether or not anything is in it, so what
// says a database has been filled is a relation somewhere in the catalogue and never a schema.
export function populated(schemas: Schema[]): boolean {
  return schemas.some((schema) => schema.relations.length > 0);
}

// A thrown value is whatever the engine felt like throwing, and a log line has to be a line either way.
export function failureText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : String(error);
}
